import { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { Download, Loader2, FileSpreadsheet } from 'lucide-react';
import { modelBuilderApi } from '../../services/modelBuilderApi';

/**
 * Convert a sheet to a 2D array of display values.
 * For each cell: show cached value (v) if present, otherwise show formula (f),
 * otherwise show raw value. This handles openpyxl-generated files where
 * formula cells have no cached value.
 */
function sheetToRows(sheet) {
  if (!sheet || !sheet['!ref']) return [];
  const range = XLSX.utils.decode_range(sheet['!ref']);
  const rows = [];
  for (let r = range.s.r; r <= range.e.r; r++) {
    const row = [];
    for (let c = range.s.c; c <= range.e.c; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = sheet[addr];
      if (!cell) {
        row.push({ value: '', type: 'empty' });
      } else if (cell.f && (cell.v === undefined || cell.v === null)) {
        // Formula cell with no cached value — show formula
        row.push({ value: `=${cell.f}`, type: 'formula' });
      } else if (cell.v !== undefined && cell.v !== null) {
        // Has a value (could be from formula or direct)
        const formatted = cell.w || String(cell.v);
        row.push({ value: formatted, type: cell.f ? 'computed' : (typeof cell.v === 'number' ? 'number' : 'text') });
      } else {
        row.push({ value: '', type: 'empty' });
      }
    }
    rows.push(row);
  }
  return rows;
}

export default function XlsxPreview({ fileId, filename }) {
  const [workbook, setWorkbook] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const previewUrl = modelBuilderApi.getPreviewUrl(fileId);
    fetch(previewUrl)
      .then(r => {
        if (!r.ok) throw new Error(`Failed to fetch: ${r.status}`);
        return r.arrayBuffer();
      })
      .then(data => {
        const wb = XLSX.read(data, { type: 'array', cellFormula: true });
        setWorkbook(wb);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [fileId]);

  const rows = useMemo(() => {
    if (!workbook) return [];
    const sheetName = workbook.SheetNames[activeSheet];
    return sheetToRows(workbook.Sheets[sheetName]);
  }, [workbook, activeSheet]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-4 border border-gray-700 rounded-lg my-2 bg-gray-800">
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        <span className="text-sm text-gray-400">Loading spreadsheet preview...</span>
      </div>
    );
  }

  if (error || !workbook) {
    return (
      <div className="flex items-center justify-between px-3 py-3 border border-gray-700 rounded-lg my-2 bg-gray-800">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4 text-emerald-400" />
          <span className="text-sm text-gray-200">{filename}</span>
          {error && <span className="text-xs text-red-400">Preview unavailable</span>}
        </div>
        <a
          href={modelBuilderApi.getDownloadUrl(fileId)}
          download
          className="flex items-center gap-1 px-2 py-1 text-xs text-blue-400 hover:text-blue-300 border border-gray-600 rounded"
        >
          <Download className="h-3 w-3" />
          Download
        </a>
      </div>
    );
  }

  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden my-2">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4 text-emerald-400" />
          <span className="text-sm font-medium text-gray-200">{filename}</span>
        </div>
        <a
          href={modelBuilderApi.getDownloadUrl(fileId)}
          download
          className="flex items-center gap-1 px-2 py-1 text-xs text-blue-400 hover:text-blue-300 border border-gray-600 rounded hover:border-blue-500 transition-colors"
        >
          <Download className="h-3 w-3" />
          Download .xlsx
        </a>
      </div>

      {/* Sheet tabs */}
      {workbook.SheetNames.length > 1 && (
        <div className="flex gap-0 bg-gray-900 border-b border-gray-700 overflow-x-auto">
          {workbook.SheetNames.map((name, i) => (
            <button
              key={name}
              onClick={() => setActiveSheet(i)}
              className={`px-3 py-1.5 text-xs whitespace-nowrap transition-colors ${
                i === activeSheet
                  ? 'bg-gray-700 text-white border-b-2 border-emerald-400'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              }`}
            >
              {name}
            </button>
          ))}
        </div>
      )}

      {/* Sheet content — custom renderer that handles formula cells */}
      <div className="overflow-auto max-h-96 bg-white">
        <table className="w-full border-collapse text-xs text-black">
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className={ri === 0 ? 'bg-gray-100 font-semibold' : 'hover:bg-blue-50'}>
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className={`border border-gray-300 px-2 py-1 whitespace-nowrap ${
                      cell.type === 'formula' ? 'text-gray-400 italic text-[10px] font-mono' :
                      cell.type === 'number' || cell.type === 'computed' ? 'text-right' : ''
                    }`}
                    title={cell.type === 'formula' ? cell.value : undefined}
                  >
                    {cell.type === 'formula'
                      ? cell.value.length > 25 ? cell.value.slice(0, 25) + '…' : cell.value
                      : cell.value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
